import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Paper, Stack, Typography,
} from "@mui/material";
import Page from "@/components/Page";
import StatusChip from "@/components/StatusChip";
import { Can } from "@/components/Guard";
import { fetcher } from "@/lib/query";
import { relative } from "@/lib/format";
import { useAuth } from "@/auth/AuthContext";
import type { SupportDashboard } from "@/features/support/types";

interface Instance { _id: string; tenant_name: string; status: string; kind: string; expires_at?: string }
interface DemoRequest { _id: string; customer: { company: string }; status: string; created_at?: string }
interface Job { _id: string; flow: string; instance_slug: string; status: string; started_at?: string }
interface Opportunity { _id: string; customer: { name: string }; status: string; updated_at?: string }

function Metric({ label, value, to, tone }: {
  label: string; value: string | number; to?: string; tone?: "warning" | "error";
}) {
  const body = (
    <CardContent sx={{ py: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography
        variant="h4" fontWeight={600}
        color={tone === "error" ? "error.main" : tone === "warning" ? "warning.main" : undefined}
      >
        {value}
      </Typography>
    </CardContent>
  );
  return (
    <Card>
      {to ? <CardActionArea component={RouterLink} to={to}>{body}</CardActionArea> : body}
    </Card>
  );
}

/** Only mounted for support.read, so the query never runs for anyone else. */
function SupportCard() {
  const dashboard = useQuery({ ...fetcher<SupportDashboard>("/support/dashboard"), refetchInterval: 60_000 });
  const data = dashboard.data;
  const figures: { label: string; value?: number; to: string; tone?: "warning" | "error" }[] = [
    { label: "Open tickets", value: data?.open_tickets, to: "/support/tickets" },
    { label: "SLA breaches", value: data?.sla_breaches, to: "/support/tickets", tone: "error" },
    { label: "Compliance alerts", value: data?.open_compliance_alerts, to: "/support/compliance", tone: "warning" },
    { label: "Unseen", value: data?.unseen_notifications, to: "/support/notifications" },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>Support</Typography>
        <Button size="small" component={RouterLink} to="/support/tickets">Tickets</Button>
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 2 }}>
        {figures.map((figure) => (
          <Box key={figure.label} component={RouterLink} to={figure.to}
               sx={{ textDecoration: "none", color: "inherit", minWidth: 0 }}>
            <Typography
              variant="h5" fontWeight={600}
              color={figure.value && figure.tone ? `${figure.tone}.main` : undefined}
            >
              {figure.value ?? "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {figure.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Stack spacing={1.5}>
        {(data?.recent_tickets ?? []).map((ticket) => (
          <Stack key={ticket._id} direction="row" spacing={1.5} alignItems="center"
                 component={RouterLink} to={`/support/tickets/${ticket._id}`}
                 sx={{ textDecoration: "none", color: "inherit" }}>
            <StatusChip status={ticket.status} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap>{ticket.rma_number} · {ticket.reason}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">{relative(ticket.created_at)}</Typography>
          </Stack>
        ))}
        {data && !data.recent_tickets.length ? (
          <Typography variant="body2" color="text.secondary">No open ticket.</Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function OverviewPage() {
  const { me, can } = useAuth();

  const instances = useQuery({ ...fetcher<{ instances: Instance[]; total: number }>("/instances?limit=200"),
                               enabled: can("instances.read") });
  const demos = useQuery({ ...fetcher<{ requests: DemoRequest[]; total: number }>("/demos?limit=200"),
                           enabled: can("demos.read") });
  const jobs = useQuery({ ...fetcher<{ jobs: Job[] }>("/jobs?limit=10"),
                          enabled: can("jobs.read"), refetchInterval: 15_000 });
  const opportunities = useQuery({
    ...fetcher<{ opportunities: Opportunity[] }>("/sales/opportunities?limit=10"),
    enabled: can("sales.read"),
  });

  const rows = instances.data?.instances ?? [];
  const active = rows.filter((row) => row.status === "active").length;
  const broken = rows.filter((row) => row.status === "failed").length;
  const busy = rows.filter((row) =>
    ["provisioning", "upgrading", "archiving", "reinstating"].includes(row.status)).length;
  const pending = (demos.data?.requests ?? []).filter((row) => row.status === "submitted");
  const expiring = rows.filter((row) => {
    if (!row.expires_at) return false;
    const days = (new Date(row.expires_at).getTime() - Date.now()) / 86_400_000;
    return days > 0 && days <= 7;
  });

  return (
    <Page
      title={`Welcome${me?.user.name ? `, ${me.user.name.split(" ")[0]}` : ""}`}
      subtitle={`${me?.org.name} · ${me?.org.kind === "vendor" ? "MultiDoc" : "Partner"}`}
    >
      <Stack spacing={3}>
        {pending.length ? (
          <Alert
            severity="warning"
            action={
              <Button size="small" component={RouterLink} to="/demos">Review</Button>
            }
          >
            {pending.length} demo request{pending.length > 1 ? "s are" : " is"} waiting for a
            decision.
          </Alert>
        ) : null}

        {broken ? (
          <Alert
            severity="error"
            action={<Button size="small" component={RouterLink} to="/instances">Look</Button>}
          >
            {broken} instance{broken > 1 ? "s" : ""} failed. Retrying resumes at the step that
            broke rather than starting over.
          </Alert>
        ) : null}

        <Box sx={{ display: "grid", gap: 2,
                   gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" } }}>
          <Can perms={["instances.read"]}>
            <Metric label="Active instances" value={active} to="/instances" />
            <Metric label="In flight" value={busy} to="/jobs" />
          </Can>
          <Can perms={["demos.read"]}>
            <Metric label="Demos awaiting a decision" value={pending.length}
                    to="/demos" tone={pending.length ? "warning" : undefined} />
          </Can>
          <Can perms={["instances.read"]}>
            <Metric label="Expiring this week" value={expiring.length}
                    to="/demos" tone={expiring.length ? "warning" : undefined} />
          </Can>
        </Box>

        <Box sx={{ display: "grid", gap: 3,
                   // minmax(0, …): a noWrap row would otherwise widen its column past a phone screen.
                   gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" } }}>
          <Can perms={["jobs.read"]}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>Recent activity</Typography>
                <Button size="small" component={RouterLink} to="/jobs">All</Button>
              </Stack>
              <Stack spacing={1.5}>
                {(jobs.data?.jobs ?? []).slice(0, 6).map((job) => (
                  <Stack key={job._id} direction="row" spacing={1.5} alignItems="center">
                    <StatusChip status={job.status} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap>
                        {job.flow} · {job.instance_slug}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {relative(job.started_at)}
                    </Typography>
                  </Stack>
                ))}
                {!jobs.data?.jobs.length ? (
                  <Typography variant="body2" color="text.secondary">Nothing has run yet.</Typography>
                ) : null}
              </Stack>
            </Paper>
          </Can>

          <Can perms={["sales.read"]}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>Pipeline</Typography>
                <Button size="small" component={RouterLink} to="/sales">All</Button>
              </Stack>
              <Stack spacing={1.5}>
                {(opportunities.data?.opportunities ?? []).slice(0, 6).map((opportunity) => (
                  <Stack key={opportunity._id} direction="row" spacing={1.5} alignItems="center"
                         component={RouterLink} to={`/sales/${opportunity._id}`}
                         sx={{ textDecoration: "none", color: "inherit" }}>
                    <Chip size="small" label={opportunity.status} variant="outlined" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap>{opportunity.customer.name}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {relative(opportunity.updated_at)}
                    </Typography>
                  </Stack>
                ))}
                {!opportunities.data?.opportunities.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing in the pipeline.{" "}
                    <RouterLink to="/sales">Start one.</RouterLink>
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Can>

          <Can perms={["support.read"]}>
            <SupportCard />
          </Can>
        </Box>
      </Stack>
    </Page>
  );
}
